import { useEffect, useState, useCallback } from 'react';
import { Download, Menu, Moon, Radio, Sparkles, X } from 'lucide-react';
import { ThanhBen, type ManHinh } from './components/ThanhBen';
import { ManTongQuan } from './screens/TongQuan';
import { ManTaoBai } from './screens/TaoBai';
import { ManTienTrinh } from './screens/TienTrinh';
import { ManLichDinhKy } from './screens/LichDinhKy';
import { ManBinhLuan } from './screens/BinhLuan';
import { ManMcpAgent } from './screens/McpAgent';
import { ManKenhNhom } from './screens/KenhNhom';
import { api, LoiMayChu } from './services/api';
import type { Phien, TrangThaiExtension } from './services/kieu';
import { avatarActor, chonTaiKhoan, khoaLuaChon, moTaActor, tenActor, tenVietTatActor } from './services/taiKhoan';

const NHAN_MAN: Record<ManHinh, string> = {
  'tong-quan': 'Tổng quan',
  'tao-bai': 'Tạo bài',
  'lich-dinh-ky': 'Lịch định kỳ',
  'kho-bai-viet': 'Kho bài viết',
  'tien-trinh': 'Tiến Trình',
  'kenh-nhom': 'Kênh & nhóm',
  'binh-luan': 'Bình luận',
  'tim-kiem-kh': 'Tìm kiếm khách hàng',
  'mcp-agent': 'MCP Agent',
};

const EXTENSION_BUILD_YEU_CAU = '20260918-tokenfix';

export default function App() {
  const [man, setMan] = useState<ManHinh>('tong-quan');
  const [moMenu, setMoMenu] = useState(false);
  const [toi, setToi] = useState(() => localStorage.getItem('giaoDien') === 'toi');

  const [phien, setPhien] = useState<Phien | null>(null);
  const [ext, setExt] = useState<TrangThaiExtension | null>(null);
  const [uidDaChon, setUidDaChon] = useState<string | null>(null);
  const [dongBoLuc, setDongBoLuc] = useState<string>('');
  const [loi, setLoi] = useState<string | null>(null);
  const [moNangCap, setMoNangCap] = useState(false);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', toi);
    localStorage.setItem('giaoDien', toi ? 'toi' : 'sang');
  }, [toi]);

  const lamMoi = useCallback(async () => {
    try {
      const [p, e] = await Promise.allSettled([api.phien(), api.trangThaiExtension()]);
      if (p.status === 'fulfilled') setPhien(p.value);
      if (e.status === 'fulfilled') setExt(e.value);
      setDongBoLuc(new Date().toLocaleTimeString('vi-VN'));
      setLoi(null);
    } catch (err) {
      setLoi(err instanceof LoiMayChu ? err.message : 'Không tải được dữ liệu.');
    }
  }, []);

  useEffect(() => {
    lamMoi();
    // Máy chủ có thể khởi động lại (deploy). Hỏi lại định kỳ để giao diện
    // không kẹt ở trạng thái cũ — đây là bài học từ BUG-5.
    const t = setInterval(lamMoi, 20000);
    return () => clearInterval(t);
  }, [lamMoi]);

  const daDangNhap = Boolean(phien?.user);
  const noiDuoc = Boolean(ext?.connected && ext?.facebookLoggedIn);
  const taiKhoan = chonTaiKhoan(ext, uidDaChon);
  const phienBanExtension = taiKhoan?.version || ext?.accounts?.[0]?.version || null;
  const buildExtension = taiKhoan?.build || ext?.accounts?.[0]?.build || null;
  const extensionCanCapNhat = noiDuoc && buildExtension !== EXTENSION_BUILD_YEU_CAU;
  useEffect(() => {
    if (!phien?.user?.tenantId) return;
    const khoa = khoaLuaChon(phien.user.tenantId);
    const uid = chonTaiKhoan(ext, localStorage.getItem(khoa))?.uid || null;
    setUidDaChon(uid);
    if (uid) localStorage.setItem(khoa, uid); else localStorage.removeItem(khoa);
  }, [ext, phien?.user?.tenantId]);

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <ThanhBen
        dangMo={man}
        doiMan={setMan}
        moTrenDienThoai={moMenu}
        dongMenu={() => setMoMenu(false)}
        daDangNhap={daDangNhap}
        onDangXuat={async () => { await api.dangXuat().catch(() => {}); location.href = '/login'; }}
      />

      {moMenu && (
        <div className="fixed inset-0 z-30 bg-slate-900/40 md:hidden" onClick={() => setMoMenu(false)} />
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-slate-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-800 sm:px-6">
          <button onClick={() => setMoMenu(true)} aria-label="Mở menu" className="rounded-lg p-2 text-slate-500 md:hidden">
            <Menu className="h-5 w-5" />
          </button>
          <h1 className="min-w-0 flex-1 truncate text-lg font-bold text-slate-900 dark:text-white sm:text-lg">
            {NHAN_MAN[man]}
          </h1>

          <div className="hidden min-w-0 items-center gap-2 rounded-full bg-slate-50 px-2.5 py-1.5 dark:bg-slate-700/50 lg:flex" title={taiKhoan ? tenActor(taiKhoan) : 'Chưa có actor Facebook'}>
            <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Actor</span>
            <span className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-200 text-[10px] font-bold text-slate-600 dark:bg-slate-600 dark:text-slate-200">
              {avatarActor(taiKhoan) ? <img src={avatarActor(taiKhoan) || ''} alt="" className="h-full w-full object-cover" /> : tenVietTatActor(taiKhoan)}
            </span>
            <span className="max-w-[190px] truncate text-xs font-semibold text-slate-700 dark:text-slate-200">{moTaActor(taiKhoan)}</span>
          </div>

          <span
            className={
              'hidden items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold sm:inline-flex ' +
              (extensionCanCapNhat
                ? 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400'
                : noiDuoc
                ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400'
                : 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400')
            }
            title={buildExtension ? `Extension ${phienBanExtension || '?'} · ${buildExtension}` : 'Chưa xác định được build Extension'}
          >
            <Radio className="h-3.5 w-3.5" />
            {noiDuoc ? `Đang kết nối${phienBanExtension ? ` · Ext ${phienBanExtension}` : ''}` : 'Thiết lập kết nối'}
            {extensionCanCapNhat ? ' · cần cập nhật' : ''}
          </span>

          <a
            href="/api/downloads/extension"
            className="hidden items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 shadow-sm transition hover:border-brand-200 hover:bg-brand-50 hover:text-brand-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-brand-500/40 dark:hover:bg-brand-500/10 dark:hover:text-brand-300 sm:inline-flex"
            title="Tải bản FBMax Companion Extension mới nhất"
          >
            <Download className="h-3.5 w-3.5" />
            {noiDuoc ? 'Cập nhật Extension' : 'Tải Extension'}
          </a>

          {phien?.entitlement?.active ? (
            <span className="hidden rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 sm:inline-flex dark:bg-emerald-500/10 dark:text-emerald-400">
              {phien.entitlement.planName} · còn {phien.entitlement.daysLeft} ngày
            </span>
          ) : (
            <button onClick={() => setMoNangCap(true)} className="hidden items-center gap-1.5 rounded-full bg-amber-400 px-3 py-1.5 text-xs font-bold text-amber-950 sm:inline-flex">
              <Sparkles className="h-3.5 w-3.5" />
              Gia hạn gói
            </button>
          )}

          <button onClick={() => setToi(v => !v)} aria-label="Đổi giao diện" className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700">
            <Moon className="h-[18px] w-[18px]" />
          </button>

          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-50 text-xs font-bold text-brand-700 dark:bg-brand-500/10 dark:text-brand-300" title={phien?.user?.email || 'Tài khoản FBMax'}>
            {(phien?.user?.name || 'FB').slice(0, 2).toUpperCase()}
          </div>
        </header>

        {moNangCap && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={() => setMoNangCap(false)}>
            <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-xl dark:border-slate-700 dark:bg-slate-800" onClick={e => e.stopPropagation()}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-bold text-slate-900 dark:text-white">Gia hạn để mở khóa thao tác</p>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Gói hiện tại: {phien?.entitlement?.planName || 'Chưa có gói'} · {phien?.entitlement?.active ? `còn ${phien.entitlement.daysLeft} ngày` : 'đã hết hạn'}.
                  </p>
                </div>
                <button onClick={() => setMoNangCap(false)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700" aria-label="Đóng">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="mt-4 rounded-xl bg-amber-50 p-4 text-sm text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">
                Tính năng vẫn được hiển thị để bạn xem trước, nhưng các hành động tạo job sẽ bị khóa cho tới khi gói được kích hoạt lại.
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <a href="/" className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700/50">Xem bảng giá</a>
                <button onClick={() => setMoNangCap(false)} className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700">Đã hiểu</button>
              </div>
            </div>
          </div>
        )}

        <main key={`${phien?.user?.tenantId || ''}:${taiKhoan?.uid || ''}`} className="min-w-0 flex-1 p-4 sm:p-6 lg:p-8">
          {(ext?.accounts.length || 0) > 1 && <select aria-label="Tài khoản Facebook" value={taiKhoan?.uid || ''} onChange={e => {
            if (!phien?.user) return;
            localStorage.setItem(khoaLuaChon(phien.user.tenantId), e.target.value);
            setUidDaChon(e.target.value);
          }}>
            <option value="" disabled>Chọn tài khoản</option>
            {ext?.accounts.map(t => <option key={t.uid} value={t.uid}>{t.name || t.uid}</option>)}
          </select>}
          {dongBoLuc && (
            <p className="mb-3 text-right text-xs text-slate-400">Đồng bộ lần cuối: {dongBoLuc}</p>
          )}
          {loi && (
            <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-400">
              {loi}
            </div>
          )}
          {man === 'tong-quan' ? (
            <ManTongQuan ext={ext} taiKhoan={taiKhoan} onLamMoi={lamMoi} onTaoBai={() => setMan('tao-bai')} />
          ) : man === 'tao-bai' ? (
            <ManTaoBai onXong={lamMoi} fbUid={taiKhoan?.uid} dangDungPage={Boolean(taiKhoan?.isPage)} />
          ) : man === 'tien-trinh' ? (
            <ManTienTrinh onLamMoi={lamMoi} />
          ) : man === 'lich-dinh-ky' ? (
            <ManLichDinhKy ext={ext} onLamMoi={lamMoi} />
          ) : man === 'binh-luan' ? (
            <ManBinhLuan onLamMoi={lamMoi} taiKhoan={taiKhoan} token={phien?.token || null} />
          ) : man === 'mcp-agent' ? (
            <ManMcpAgent ext={ext} />
          ) : man === 'kenh-nhom' ? (
            <ManKenhNhom taiKhoan={taiKhoan} />
          ) : (
            <ChuaDung nhan={NHAN_MAN[man]} />
          )}
        </main>
      </div>
    </div>
  );
}

function ChuaDung({ nhan }: { nhan: string }) {
  return (
    <div className="mx-auto max-w-[1440px] animate-fade-in">
      <div className="rounded-xl border border-slate-200/80 bg-white p-6 shadow-card dark:border-slate-700/60 dark:bg-slate-800">
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Màn <span className="font-semibold text-slate-700 dark:text-slate-200">{nhan}</span> đang được dựng lại.
        </p>
      </div>
    </div>
  );
}
